/**
 * useDashboardNavigation - Centralized hook for dashboard navigation.
 *
 * This hook provides a single source of truth for dashboard navigation,
 * using the URL as the source of truth for the current dashboard ID.
 *
 * Key principles:
 * - URL is the single source of truth (or panel route in split view)
 * - No sync between stores needed
 * - Browser history (back/forward) works naturally
 * - Deep linking works out of the box
 * - Works correctly in split view panels via PanelRouteContext
 * - Panel-aware navigation: updates only the active panel in split view
 */

import { useCallback, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router";
import { usePanelNavigation, useResourceId, useGlobalResourceId } from "@/hooks/use-panel-route";

interface DashboardNavigationOptions {
  /** Replace current history entry instead of pushing */
  replace?: boolean;
  /** Preserve existing search params */
  preserveParams?: boolean;
}

interface DashboardNavigationResult {
  /** Current dashboard ID from URL (null if none) */
  currentDashboardId: string | null;
  /** Whether we're on the dashboards page */
  isOnDashboardsPage: boolean;
  /** Navigate to a specific dashboard */
  goToDashboard: (id: string, options?: DashboardNavigationOptions) => void;
  /** Navigate to dashboard gallery (no dashboard selected) */
  goToGallery: (options?: DashboardNavigationOptions) => void;
  /** Navigate to create new dashboard */
  goToCreate: (options?: DashboardNavigationOptions) => void;
  /** Navigate to create from template */
  goToTemplate: (options?: DashboardNavigationOptions) => void;
  /** Navigate to dashboard settings */
  goToSettings: (dashboardId: string, options?: DashboardNavigationOptions) => void;
  /** Clear dashboard selection (go back to gallery) */
  clearSelection: (options?: DashboardNavigationOptions) => void;
}

/**
 * Hook for centralized dashboard navigation.
 * Uses URL search params as the single source of truth.
 * In split view panels, uses the panel's route instead of the browser URL.
 * Navigation is panel-aware: updates only the active panel in split view.
 */
export function useDashboardNavigation(): DashboardNavigationResult {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Use panel-aware navigation - this handles split view correctly
  const { navigateTo } = usePanelNavigation();
  
  // Use unified resource ID hook (panel-aware)
  const currentDashboardId = useResourceId();
  const isOnDashboardsPage = location.pathname.startsWith("/dashboards");

  // Navigate to a specific dashboard
  const goToDashboard = useCallback(
    (id: string, options?: DashboardNavigationOptions) => {
      const { replace = false, preserveParams = false } = options || {};

      if (preserveParams) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("id", id);
        newParams.delete("new");
        newParams.delete("template");
        navigateTo(`/dashboards?${newParams.toString()}`, { replace });
      } else {
        navigateTo(`/dashboards?id=${id}`, { replace });
      }
    },
    [navigateTo, searchParams]
  );

  // Navigate to dashboard gallery
  const goToGallery = useCallback(
    (options?: DashboardNavigationOptions) => {
      const { replace = false } = options || {};
      navigateTo("/dashboards", { replace });
    },
    [navigateTo]
  );

  // Navigate to create new dashboard
  const goToCreate = useCallback(
    (options?: DashboardNavigationOptions) => {
      const { replace = true } = options || {};
      navigateTo("/dashboards?new=true", { replace });
    },
    [navigateTo]
  );

  // Navigate to create from template
  const goToTemplate = useCallback(
    (options?: DashboardNavigationOptions) => {
      const { replace = true } = options || {};
      navigateTo("/dashboards?template=true", { replace });
    },
    [navigateTo]
  );

  // Navigate to dashboard settings
  const goToSettings = useCallback(
    (dashboardId: string, options?: DashboardNavigationOptions) => {
      const { replace = false } = options || {};
      navigateTo(`/dashboards?id=${dashboardId}&settings=true`, { replace });
    },
    [navigateTo]
  );

  // Clear selection (go back to gallery)
  const clearSelection = useCallback(
    (options?: DashboardNavigationOptions) => {
      const { replace = false } = options || {};
      navigateTo("/dashboards", { replace });
    },
    [navigateTo]
  );

  return useMemo(
    () => ({
      currentDashboardId,
      isOnDashboardsPage,
      goToDashboard,
      goToGallery,
      goToCreate,
      goToTemplate,
      goToSettings,
      clearSelection,
    }),
    [
      currentDashboardId,
      isOnDashboardsPage,
      goToDashboard,
      goToGallery,
      goToCreate,
      goToTemplate,
      goToSettings,
      clearSelection,
    ]
  );
}

/**
 * Simple hook to just get the current dashboard ID from URL or panel route.
 * Use this when you only need to read, not navigate.
 * Works correctly in split view panels.
 */
export function useCurrentDashboardId(): string | null {
  return useResourceId();
}

/**
 * Simple hook to check if a specific dashboard is active.
 * Use this for highlighting active items in sidebar.
 * Note: This uses the global URL, not panel route, since it's for sidebar highlighting.
 */
export function useIsDashboardActive(dashboardId: string | undefined): boolean {
  const globalId = useGlobalResourceId();
  if (!dashboardId) return false;
  return globalId === dashboardId;
}

export default useDashboardNavigation;
